import React, { useEffect, useState } from "react";
import {
  Container,
  Typography,
  Box,
  Avatar,
  Button,
  Grid,
  TextField,
  Paper,
  List,
  ListItem,
  ListItemText,
  Divider,
  Stack,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  Alert,
  IconButton,
} from "@mui/material";
import { useAuth } from "../context/AuthContext";
import { db, storage } from "../utils/firebase";
import {
  doc,
  getDoc,
  updateDoc,
  collection,
  getDocs,
  setDoc,
  query,
  where,
  onSnapshot,
  serverTimestamp
} from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { useNavigate, Navigate } from "react-router-dom";
import EventSignupQuestions from "../components/EventSignupQuestions";
import ViewSignupAnswersDialog from "../components/ViewSignupAnswersDialog";
import { notifyAdmins } from "../utils/adminNotify";

export default function AccountPage() {
  const [viewSignupDialogOpen, setViewSignupDialogOpen] = useState(false);
  const [viewSignupEventId, setViewSignupEventId] = useState(null);
  const [signupDialogOpen, setSignupDialogOpen] = useState(false);
  const [signupEvent, setSignupEvent] = useState(null);
  const [signupAnswers, setSignupAnswers] = useState({});
  const [signupTouched, setSignupTouched] = useState({});
  const allSignupFieldsValid = React.useMemo(() => {
    const v = signupAnswers || {};
    const required = [
      'within2Hours',
      'whyAttend',
      'jesusRelationship',
      'nickname',
      'legallySingle',
      'church',
      'personalityWords',
      'hasCharges',
      'backgroundInfo',
      'paymentMethod',
    ];
    return v.agreementAccepted === true && required.every(k => {
      const val = v[k];
      return typeof val === 'string' ? (val.trim().length > 0) : val != null && val !== '';
    });
  }, [signupAnswers]);
  const signupErrors = React.useMemo(() => {
    const v = signupAnswers || {};
    const errs = {};
    const req = (k, msg='Required') => { const val = v[k]; if ((typeof val === 'string' && !val.trim()) || val === '' || val == null) errs[k] = msg; };
    req('within2Hours');
    req('whyAttend');
    req('jesusRelationship');
    req('nickname');
    req('legallySingle');
    req('church');
    req('personalityWords');
    req('hasCharges');
    req('backgroundInfo');
    req('paymentMethod');
    if (v.agreementAccepted !== true) errs.agreementAccepted = 'You must accept the agreement.';
    return errs;
  }, [signupAnswers]);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [cancelEvent, setCancelEvent] = useState(null);
  const { user, role, logout, sendResetPasswordEmail, sendVerificationEmail } = useAuth();
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [editValues, setEditValues] = useState({});
  const [events, setEvents] = useState([]);
  const [myEvents, setMyEvents] = useState([]);
  const [participants, setParticipants] = useState([]);
  // Track whether profile is in edit mode
  const [edit, setEdit] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });
  const [sendingVerification, setSendingVerification] = useState(false);

  const handlePasswordResetEmail = async () => {
    if (!user?.email) return;
    try {
      await sendResetPasswordEmail(user.email);
      setSnackbar({ open: true, message: 'Password reset email sent.', severity: 'success' });
    } catch (err) {
      setSnackbar({ open: true, message: 'Unable to send password reset email.', severity: 'error' });
    }
  };

  const handleVerificationEmail = async () => {
    if (!user) return;
    try {
      setSendingVerification(true);
      await sendVerificationEmail(user);
      setSnackbar({ open: true, message: 'Verification email sent.', severity: 'success' });
    } catch (err) {
      setSnackbar({ open: true, message: 'Unable to send verification email.', severity: 'error' });
    }
    setSendingVerification(false);
  };
  useEffect(() => {
    if (!user) return;
    // Real-time subscription to user profile
    const userRef = doc(db, "users", user.uid);
    const unsubUser = onSnapshot(userRef, (snap) => {
      if (snap.exists()) {
        const data = snap.data();
        setProfile(data);
        setEditValues(data);
      }
    });
    // One-time fetch for events + participants (could also be realtime later)
    const fetchEventsAndParticipants = async () => {
      const qPart = query(collection(db, "participants"), where("userId", "==", user.uid));
      const partSnap = await getDocs(qPart);
      const parts = partSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      const signedEventIds = new Set(parts.map(p => p.eventId).filter(Boolean));

      const eventsSnap = await getDocs(collection(db, "events"));
      const eventsArr = eventsSnap.docs
        .map((doc) => ({ id: doc.id, ...doc.data() }))
        .filter(ev => ev.archived !== true)
        .filter(ev => ev.visible !== false || signedEventIds.has(ev.id));

      const eventsWithMyPart = eventsArr.map(ev => {
        const myPart = parts.find(p => p.eventId === ev.id) || null;
        return { ...ev, myPart };
      });
  // Upcoming events exclude those where user already has a participant (except cancelled)
  setEvents(eventsWithMyPart.filter(ev => !(ev.myPart && ev.myPart.status !== 'cancelled')));
  // My events include only active signups (non-cancelled)
  setMyEvents(eventsWithMyPart.filter(ev => ev.myPart && ev.myPart.status !== 'cancelled'));
      setParticipants(parts);
    };
    fetchEventsAndParticipants();
    return () => {
      unsubUser();
    };
  }, [user, role]);

  if (!profile) return <CircularProgress />;

  return (
    <Container maxWidth="lg" sx={{ mt: 4 }}>
      <Grid container spacing={3}>
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: { xs: 2, sm: 3 }, mb: { xs: 2, sm: 3 } }}>
            <Box display="flex" flexDirection="column" alignItems="center">
              {user && !user.emailVerified && (
                <Alert
                  severity="warning"
                  sx={{ width: '100%', mb: 2 }}
                  action={
                    <Button color="inherit" size="small" onClick={handleVerificationEmail} disabled={sendingVerification}>
                      Resend email
                    </Button>
                  }
                >
                  Your email address is not verified yet.
                </Alert>
              )}
              {(() => {
                const hasPending = !!(profile.pendingProfilePicture && profile.profilePictureApproved === false);
                // Consolidate possible approved fields
                const approvedCandidates = [
                  profile.approvedProfilePicture,
                  profile.profilePicture,
                  profile.profilePictureUrl,
                  profile.photoURL
                ].filter(Boolean);
                const approvedSrc = approvedCandidates.length ? approvedCandidates[0] : undefined;
                let avatarSrc;
                if (hasPending) {
                  avatarSrc = profile.pendingProfilePicture;
                } else if (profile.profilePictureApproved === true && approvedSrc) {
                  avatarSrc = approvedSrc;
                } else if (approvedSrc) {
                  avatarSrc = approvedSrc;
                } else if (profile.pendingProfilePicture) {
                  avatarSrc = profile.pendingProfilePicture;
                } else {
                  avatarSrc = undefined;
                }
                // Local reconciliation: if marked approved but only pending exists, reuse it.
                if (profile.profilePictureApproved === true && !approvedSrc && profile.pendingProfilePicture) {
                  avatarSrc = profile.pendingProfilePicture;
                }
                return (
                  <Avatar
                    sx={{
                      width: 118,
                      height: 118,
                      mb: 1.5,
                      border: hasPending ? '3px solid #FFA726' : '2px solid #e0e0e0',
                      boxShadow: 2,
                      backgroundColor: '#fff',
                      transition: 'border-color 0.25s'
                    }}
                    src={avatarSrc}
                  />
                );
              })()}
              {profile.pendingProfilePicture && profile.profilePictureApproved === false && (
                <Typography
                  variant="body2"
                  sx={{
                    color: '#FFA726',
                    fontWeight: 500,
                    fontSize: 15,
                    mb: 1.5,
                    fontFamily: 'Roboto, Helvetica, Arial, sans-serif'
                  }}
                >
                  Profile picture is pending admin approval
                </Typography>
              )}
              <Button
                variant="outlined"
                size="small"
                component="label"
                sx={{ mt: 1 , mb: 5}}
              >
                Change Profile Picture
                <input
                  type="file"
                  accept="image/*"
                  hidden
                  onChange={async e => {
                    if (e.target.files && e.target.files[0]) {
                      const file = e.target.files[0];
                      try {
                        const storageRef = ref(storage, `profilePictures/${user.uid}/${Date.now()}_${file.name}`);
                        await uploadBytes(storageRef, file);
                        const url = await getDownloadURL(storageRef);
                        // Immediate optimistic update so user sees it right away
                        setProfile(prev => prev ? { ...prev, pendingProfilePicture: url, profilePictureApproved: false } : prev);
                        await updateDoc(doc(db, "users", user.uid), {
                          pendingProfilePicture: url,
                          profilePictureApproved: false
                        });
                        // Notify admins (non-blocking)
                        notifyAdmins('profile_picture_submitted', { userId: user.uid, email: profile?.email, name: profile?.name, url });
                        // Removed popup alert; inline pending notice now displayed beneath avatar.
                      } catch (err) {
                        setSnackbar({ open: true, message: 'Failed to upload profile picture: ' + err.message, severity: 'error' });
                      }
                    }
                  }}
                />
              </Button>
              <Button variant="text" size="small" sx={{ mb: 2 }} onClick={handlePasswordResetEmail}>
                Email me a password reset link
              </Button>
              <Typography variant="h6">{profile.name}</Typography>
              <Typography variant="body2">{profile.email}</Typography>
              <Typography variant="body2">{profile.city}, {profile.state}</Typography>
              <Typography variant="body2">{profile.phone}</Typography>
              {profile.occupation && (
                <Typography variant="body2">Occupation: {profile.occupation}</Typography>
              )}
              {!edit && (
                <Button variant="outlined" sx={{ mt: 2 }} onClick={() => setEdit(true)} fullWidth>
                  Edit Profile
                </Button>
              )}
              {edit && (
                <Box component="form" onSubmit={async e => {
                  e.preventDefault();
                  try {
                    await updateDoc(doc(db, "users", user.uid), {
                      name: editValues.name || '',
                      city: editValues.city || '',
                      state: editValues.state || '',
                      phone: editValues.phone || '',
                      occupation: editValues.occupation || ''
                    });
                    setEdit(false);
                  } catch (err) {
                    setSnackbar({ open: true, message: 'Failed to save profile: ' + err.message, severity: 'error' });
                  }
                }} sx={{ mt: 2, width: '100%' }}>
                  <TextField
                    label="Name"
                    fullWidth
                    margin="dense"
                    value={editValues.name || ''}
                    onChange={e => setEditValues(v => ({ ...v, name: e.target.value }))}
                  />
                  <TextField
                    label="City"
                    fullWidth
                    margin="dense"
                    value={editValues.city || ''}
                    onChange={e => setEditValues(v => ({ ...v, city: e.target.value }))}
                  />
                  <TextField
                    label="State"
                    fullWidth
                    margin="dense"
                    value={editValues.state || ''}
                    onChange={e => setEditValues(v => ({ ...v, state: e.target.value }))}
                  />
                  <TextField
                    label="Occupation"
                    fullWidth
                    margin="dense"
                    value={editValues.occupation || ''}
                    onChange={e => setEditValues(v => ({ ...v, occupation: e.target.value }))}
                  />
                  <TextField
                    label="Phone"
                    fullWidth
                    margin="dense"
                    value={editValues.phone || ''}
                    onChange={e => setEditValues(v => ({ ...v, phone: e.target.value }))}
                  />
                  <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
                    <Button type="submit" variant="contained">Save</Button>
                    <Button variant="text" onClick={() => { setEdit(false); setEditValues(profile); }}>Cancel</Button>
                  </Stack>
                </Box>
              )}
            </Box>
          </Paper>
        </Grid>
        <Grid item xs={12} md={8}>
          <Paper sx={{ p: { xs: 2, sm: 3 }, mb: { xs: 2, sm: 3 } }}>
            <Typography variant="h5" gutterBottom>Upcoming Events</Typography>
            {events.length === 0 ? (
              <Typography>No upcoming events found.</Typography>
            ) : (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, justifyContent: { xs: 'center', sm: 'flex-start' } }}>
                {events.map((ev) => {
                  const myPart = ev.myPart;
                  return (
                    <Box
                      key={ev.id}
                      sx={{
                        minWidth: { xs: '90vw', sm: 260 },
                        maxWidth: { xs: '100vw', sm: 340 },
                        flex: '1 1 260px',
                        bgcolor: '#fff',
                        border: '1px solid #e0e0e0',
                        borderRadius: 2,
                        boxShadow: 1,
                        p: 2,
                        mb: 2,
                        cursor: 'pointer',
                        transition: 'box-shadow 0.2s',
                        '&:hover': { boxShadow: 4, borderColor: '#1976d2' },
                        display: 'flex', flexDirection: 'column', justifyContent: 'space-between',
                      }}
                      onClick={e => {
                        if (signupDialogOpen) {
                          e.stopPropagation();
                          return;
                        }
                        navigate(`/event/${ev.id}`);
                      }}
                    >
                      <Typography variant="h6" fontWeight={700} sx={{ color: '#1976d2', mb: 1 }}>{ev.name}</Typography>
                      <Typography variant="body2" sx={{ mb: 1 }}>
                        Date: {ev.date ? (ev.date.seconds ? new Date(ev.date.seconds * 1000) : ev.date.toDate ? ev.date.toDate() : new Date(ev.date)).toLocaleDateString() : ''}
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        <Typography variant="body2">
                          Location: {myPart && myPart.status === 'approved_paid' ? (ev.specificLocation || ev.generalLocation || 'TBA') : (ev.generalLocation || 'TBA')}
                        </Typography>
                        {myPart && myPart.status === 'approved_paid' && (ev.specificLocation || ev.generalLocation) && (
                          <IconButton
                            size="small"
                            aria-label="Open in Google Maps"
                            onClick={e => {
                              e.stopPropagation();
                              const addr = encodeURIComponent(ev.specificLocation || ev.generalLocation);
                              window.open(`https://www.google.com/maps?q=${addr}`, '_blank');
                            }}
                          >
                            <svg xmlns="http://www.w3.org/2000/svg" height="20" viewBox="0 0 24 24" width="20" fill="#4285F4"><path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/></svg>
                          </IconButton>
                        )}
                      </Box>
                      {myPart ? (
                        <Button variant="outlined" disabled size="small" sx={{ mt: 1 }}>
                          {myPart.status === 'pending' && 'Pending Approval'}
                          {myPart.status === 'approved_pending_payment' && 'Approved - Pending Payment'}
                          {myPart.status === 'approved_paid' && 'Approved & Paid'}
                          {myPart.status === 'cancelled' && 'Cancelled'}
                          {!['pending','approved_pending_payment','approved_paid','cancelled'].includes(myPart.status) && 'Signed Up'}
                        </Button>
                      ) : (
                        (() => {
                          if (role === 'admin') {
                            return (
                              <Button variant="outlined" size="small" sx={{ mt: 1 }} disabled>
                                Admins cannot join
                              </Button>
                            );
                          }
                          const userGender = (profile.gender || '').toLowerCase().trim();
                          const limit = (ev.signupLimit || '').toLowerCase().trim();
                          if (ev.mode === 'signups' || (ev.mode === 'limited-signups' && (limit === userGender))) {
                            return (
                              <Button
                                variant="contained"
                                size="small"
                                sx={{ mt: 1 }}
                                onClick={e => {
                                  e.stopPropagation();
                                  setSignupEvent(ev);
                                  setSignupAnswers({});
                                  setSignupDialogOpen(true);
                                }}
                              >
                                Sign Up
                              </Button>
                            );
                          } else {
                            return (
                              <Button variant="outlined" size="small" sx={{ mt: 1 }} disabled>
                                Sign-ups Closed
                              </Button>
                            );
                          }
                        })()
                      )}
                    </Box>
                  );
                })}
              </Box>
            )}
          </Paper>
          <Paper sx={{ p: { xs: 2, sm: 3 } }}>
            <Typography variant="h5" gutterBottom>My Events</Typography>
            {myEvents.length === 0 ? (
              <Typography>You haven't signed up for any events yet.</Typography>
            ) : (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, justifyContent: { xs: 'center', sm: 'flex-start' } }}>
                {myEvents.map((ev) => {
                  const myPart = ev.myPart;
                  return (
                    <Box
                      key={ev.id}
                      sx={{
                        minWidth: { xs: '90vw', sm: 260 },
                        maxWidth: { xs: '100vw', sm: 340 },
                        flex: '1 1 260px',
                        bgcolor: '#f9f9f9',
                        border: '1px solid #e0e0e0',
                        borderRadius: 2,
                        boxShadow: 1,
                        p: 2,
                        mb: 2,
                        cursor: 'pointer',
                        transition: 'box-shadow 0.2s',
                        '&:hover': { boxShadow: 4, borderColor: '#1976d2' },
                        display: 'flex', flexDirection: 'column', justifyContent: 'space-between',
                      }}
                      onClick={() => navigate(`/event/${ev.id}`)}
                    >
                      <Typography variant="h6" fontWeight={700} sx={{ color: '#1976d2', mb: 1 }}>{ev.name}</Typography>
                      <Typography variant="body2" sx={{ mb: 1 }}>
                        Date: {ev.date ? (ev.date.seconds ? new Date(ev.date.seconds * 1000) : ev.date.toDate ? ev.date.toDate() : new Date(ev.date)).toLocaleDateString() : ''}
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        <Typography variant="body2">
                          Location: {myPart && myPart.status === 'approved_paid' ? (ev.specificLocation || ev.generalLocation || 'TBA') : (ev.generalLocation || 'TBA')}
                        </Typography>
                        {myPart && myPart.status === 'approved_paid' && (ev.specificLocation || ev.generalLocation) && (
                          <IconButton
                            size="small"
                            aria-label="Open in Google Maps"
                            onClick={e => {
                              e.stopPropagation();
                              const addr = encodeURIComponent(ev.specificLocation || ev.generalLocation);
                              window.open(`https://www.google.com/maps?q=${addr}`, '_blank');
                            }}
                          >
                            <svg xmlns="http://www.w3.org/2000/svg" height="20" viewBox="0 0 24 24" width="20" fill="#4285F4"><path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/></svg>
                          </IconButton>
                        )}
                      </Box>
                      <Button
                        variant="outlined"
                        size="small"
                        sx={{ mt: 1, mb: 1 }}
                        onClick={e => {
                          e.stopPropagation();
                          setViewSignupEventId(ev.id);
                          setViewSignupDialogOpen(true);
                        }}
                      >
                        View Signup Form
                      </Button>
                      {/* Show button to open in-progress match form on event page */}
                      {ev.mode === 'in-progress' && myPart && myPart.status === 'approved_paid' && (
                        <Button
                          variant="contained"
                          color="primary"
                          size="small"
                          sx={{ mt: 1 }}
                          onClick={e => {
                            e.stopPropagation();
                            navigate(`/event/${ev.id}?openMatchForm=1`);
                          }}
                        >
                          { /* If previously submitted allow update wording - minimal client side hint */ }
                          Submit / Update Match Form
                        </Button>
                      )}
                      {myPart && myPart.status !== 'cancelled' && (
                        <Button
                          variant="outlined"
                          color="error"
                          size="small"
                          sx={{ mt: 1 }}
                          onClick={e => {
                            e.stopPropagation();
                            setCancelEvent(ev);
                            setCancelDialogOpen(true);
                          }}
                        >
                          Cancel Participation
                        </Button>
                      )}
                    </Box>
                  );
                })}
              </Box>
            )}
          </Paper>
        </Grid>
      </Grid>
      {/* View Signup Form Dialog */}
      <ViewSignupAnswersDialog
        open={viewSignupDialogOpen}
        onClose={() => setViewSignupDialogOpen(false)}
        eventId={viewSignupEventId}
        userId={user?.uid}
        eventMode={(myEvents.find(e => e.id === viewSignupEventId)?.mode) || 'signups'}
      />

      {/* Event Sign Up Dialog */}
      <Dialog open={signupDialogOpen} onClose={() => setSignupDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Sign Up for Event</DialogTitle>
        <DialogContent>
          <Typography gutterBottom>
            {signupEvent ? signupEvent.name : ''}
          </Typography>
          <EventSignupQuestions
            values={signupAnswers}
            onChange={e => {
              const { name, value, type, checked } = e.target;
              setSignupAnswers(prev => ({
                ...prev,
                [name]: type === 'checkbox' ? checked : value
              }));
            }}
            onBlur={e => {
              const { name } = e.target;
              if (name) setSignupTouched(t => ({ ...t, [name]: true }));
            }}
            readOnly={false}
            errors={signupErrors}
            touched={signupTouched}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSignupDialogOpen(false)} color="secondary">Cancel</Button>
          <Button
            onClick={async () => {
              if (!signupEvent || !user) return;
              if (!allSignupFieldsValid) {
                setSnackbar({ open: true, severity: 'error', message: 'Please complete all required fields and accept the user agreement.' });
                return;
              }
              try {
                const answersWithDefaults = { ...signupAnswers, ticketType: 'premium' };
                const participantDocRef = doc(collection(db, "participants"));
                await setDoc(participantDocRef, {
                  eventId: signupEvent.id,
                  userId: user.uid,
                  gender: (profile?.gender || '').toLowerCase(),
                  status: "pending",
                  answers: answersWithDefaults,
                  createdAt: serverTimestamp(),
                });
                // No event document write (restricted by rules to admins only)
                const newParticipant = {
                  id: participantDocRef.id,
                  eventId: signupEvent.id,
                  userId: user.uid,
                  gender: (profile?.gender || '').toLowerCase(),
                  status: 'pending',
                  answers: answersWithDefaults,
                  createdAt: { seconds: Math.floor(Date.now()/1000), nanoseconds: 0 }
                };
                // Update participants list
                setParticipants(prev => [...prev, newParticipant]);
                // Update events: add myPart to the event object then move it to myEvents and remove from upcoming
                setEvents(prev => prev.filter(ev => ev.id !== signupEvent.id));
                setMyEvents(prev => {
                  const enrichedEvent = { ...signupEvent, myPart: newParticipant };
                  return [...prev, enrichedEvent];
                });
                // Notify admins (non-blocking)
                notifyAdmins('event_signup', { userId: user.uid, email: profile?.email, name: profile?.name, eventId: signupEvent.id, eventName: signupEvent.name });
                setSignupDialogOpen(false);
                setSignupEvent(null);
                setSignupAnswers({});
                setSnackbar({ open: true, severity: 'success', message: 'Signup submitted!' });
              } catch (e) {
                setSnackbar({ open: true, severity: 'error', message: 'Failed to submit signup: ' + e.message });
              }
            }}
            color="primary"
            variant="contained"
            disabled={!allSignupFieldsValid}
          >
            Submit
          </Button>
        </DialogActions>
      </Dialog>
      {/* Cancel Participation Confirmation Dialog */}
      <Dialog open={cancelDialogOpen} onClose={() => setCancelDialogOpen(false)}>
        <DialogTitle>Cancel Participation</DialogTitle>
        <DialogContent>
          <Typography>Are you sure you want to cancel your participation in this event?</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCancelDialogOpen(false)} color="primary">No</Button>
          <Button color="error" onClick={async () => {
            if (cancelEvent) {
              const q = query(
                collection(db, "participants"),
                where("eventId", "==", cancelEvent.id),
                where("userId", "==", user.uid)
              );
              const snap = await getDocs(q);
              const toUpdate = snap.docs[0];
              if (toUpdate) {
                await updateDoc(doc(db, "participants", toUpdate.id), { status: 'cancelled' });
              }
            }
            setCancelDialogOpen(false);
            setCancelEvent(null);
          }}>Yes, Cancel</Button>
        </DialogActions>
      </Dialog>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={() => setSnackbar(s => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar(s => ({ ...s, open: false }))}
          severity={snackbar.severity}
          elevation={3}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
}